// ML001-F0219 (WebUI #170): the credit application settings card. One component for three levels: the organisation default (on the
// Ledger settings page), a customer (on the member account) and one credit note (inside the credit-note drawer, a per-note override).
// Mode precedence is the Ledger's: credit > customer > org > the default (automatic at 100%). Reads and writes go through Core's generic
// module relay (creditPolicyApi). Save needs the step-up the SAL 4 pattern uses: a STEP_UP_REQUIRED challenge opens the StepUpDialog and
// the SAME attempt (same Idempotency-Key) is retried on completion. A refusal from the Ledger is shown VERBATIM, never reworded.
// Without ledger_manage the card renders no policy: the Ledger does not answer the policy read to other roles.
// Wire (Spec core-ledger-account-gateway 1.12.0): the read answers CreditPolicyGetData, the write CreditPolicySetData; a refusal is shown verbatim.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { creditPolicyApi } from '../../services/creditPolicyApi';
import { refusalText } from './ledgerRefusal';
import { classifyRelayOutcome } from '../../services/relayOutcome';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import {
  POLICY_LEVEL_LABEL,
  FOLLOW_CUSTOMER,
  AUTOMATIC_MODE,
  MANUAL_MODE,
  MANUAL_LINE,
  creditApplicationOf,
  buildPolicyChanges,
  followsCustomer,
  formFromPolicy,
  hasChanges,
  lostTerms,
  modeLabel,
  PERCENT_OF_INVOICE,
  isPercentOfInvoiceShareEnabled,
  isPercentShare,
  modeOptionsFor,
  CUSTOMER_STATES,
  customerStateLabel,
  customerStateOf,
  termsSummary,
  validatePolicyForm,
} from './creditPolicyModel';
import './CreditNotesPanel.css';

const newKey = () => globalThis.crypto?.randomUUID?.() || `cp-${Date.now()}-${Math.random()}`;
// Features: M004-F0775
// Tests: T-M004-0379
// M004-F0775 (WebUI #248): an unknown outcome means the Ledger may have saved the policy. It is not a failure: the card re-reads and keeps
// the attempt's Idempotency-Key, so the next press with the same settings is the same attempt.
const UNCONFIRMED_SAVE = "We couldn't confirm whether the credit settings were saved. We've refreshed what the Ledger shows — check it before trying again.";
const MODE_SOURCE = Object.freeze({ credit: 'this credit note', customer: 'this customer', org: 'the organisation default', default: 'the organisation default' });

function FieldError({ id, message }) {
  return message ? <small className="cn-field-error" id={id} role="alert">{message}</small> : null;
}

export default function CreditPolicyCard({ orgId, level, userId, creditNoteId, canManage, title, ownAccount = false, onSaved }) {
  const [answer, setAnswer] = useState(null); // the Ledger's credit_policy object: { level, record, effective }
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState('');
  const [stepUp, setStepUp] = useState(null);
  const attempt = useRef(null); // { fingerprint, key, body } so a step-up retry re-sends the SAME body and key
  const retry = useRef(null);
  // The Ledger requires the customer user_id for a customer AND a credit scope (a credit must name the note's customer). WebUI #189.
  const customerScoped = level === 'customer' || level === 'credit';
  const scope = {
    level,
    ...(customerScoped && userId != null ? { user_id: String(userId) } : {}),
    ...(level === 'credit' ? { credit_note_id: creditNoteId } : {}),
  };
  const scopeKey = JSON.stringify(scope);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      // Core relays the Spec 1.12.0 TunnelCreditPolicyGetPayload: { binding_generation, orgId, organisationUuid, data: { credit_policy } }.
      const payload = (await creditPolicyApi.get(orgId, JSON.parse(scopeKey)))?.data?.data?.payload ?? null;
      const fresh = payload?.data?.credit_policy || null;
      setAnswer(fresh);
      return fresh;
    } catch (cause) {
      setAnswer(null);
      setLoadError(refusalText(cause, 'The credit settings could not be loaded.'));
      return null;
    }
  }, [orgId, scopeKey]);

  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  // The invoice share is offered once the flag is on (Ledger accepted it on prod), or when the record already holds it.
  const invoiceShare = form?.discount_type === PERCENT_OF_INVOICE;
  const showInvoiceShare = isPercentOfInvoiceShareEnabled() || invoiceShare;
  // Manual: the customer decides, so the Share, the amounts and both caps are disabled (their values are kept, not sent).
  const customerLevel = level === 'customer';
  const manual = !customerLevel && form?.mode === MANUAL_MODE;
  const showTerms = !customerLevel || form?.customer_state === 'custom';

  if (!canManage) {
    return (
      <section className="cn-policy" data-testid={`cp-card-${level}`} aria-label={title}>
        <h4>{title}</h4>
        <p className="cn-muted" data-testid={`cp-no-access-${level}`}>Credit application settings are shown to billing managers only.</p>
      </section>
    );
  }

  const effective = answer?.effective || null;
  const mode = effective?.mode || 'auto';
  // A credit with no mode of its own says so, and names the mode it inherits (the Ledger's precedence: credit > customer > org).
  const modeText = followsCustomer(answer) ? `${FOLLOW_CUSTOMER.label} (${modeLabel(mode)})` : modeLabel(mode);
  const source = MODE_SOURCE[effective?.mode_origin || 'default'] || MODE_SOURCE.default; // PolicyEffective.mode_origin: credit | customer | org | default

  const startEdit = () => { setForm(formFromPolicy(answer)); setErrors({}); setSaveError(''); setNotice(''); setEditing(true); };
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  // The credit-application control writes the Mode select's own value, so the two always agree.
  const setApplication = (mode) => setForm((current) => ({ ...current, mode }));
  // Choosing the invoice share clears the cap per booking and keeps its value; switching back restores that value (never sent meanwhile).
  const setShare = (event) => {
    const next = event.target.value;
    setForm((current) => {
      if (next === PERCENT_OF_INVOICE && current.discount_type !== PERCENT_OF_INVOICE) return { ...current, discount_type: next, max_per_booking_kept: current.max_per_booking, max_per_booking: '' };
      if (next !== PERCENT_OF_INVOICE && current.discount_type === PERCENT_OF_INVOICE) return { ...current, discount_type: next, max_per_booking: current.max_per_booking_kept };
      return { ...current, discount_type: next };
    });
  };

  const runSave = async (body, key) => {
    setSaving(true); setSaveError(''); setNotice('');
    try {
      await creditPolicyApi.set(orgId, body, key);
      attempt.current = null;
      setEditing(false);
      const fresh = await load();
      // Read-back: a Ledger that accepts a term and does not keep it must not read as Saved (owner bug 2026-10-14).
      const lost = lostTerms(body.changes || {}, fresh);
      if (lost.length) setSaveError(`The Ledger did not keep: ${lost.join(', ').replace(/_/g, ' ')}. What is shown is what the Ledger holds now; nothing else was changed.`);
      else setNotice('Saved.');
      if (onSaved) onSaved();
    } catch (cause) {
      const status = Number(cause?.response?.status);
      // Step-up is the 401 challenge; a 403 is a refusal and never opens the dialog.
      const challenge = status === 403 ? null : getStepUpChallengeFromError(cause);
      if (challenge?.id) {
        retry.current = { body, key };
        setStepUp(challenge);
      } else if (classifyRelayOutcome(cause) === 'unknown') {
        setNotice(UNCONFIRMED_SAVE);
        await load();
      } else {
        attempt.current = null;
        setSaveError(refusalText(cause, 'The credit settings could not be saved. Nothing was changed.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    const found = validatePolicyForm(form, level);
    setErrors(found);
    setSaveError('');
    if (Object.keys(found).length) return;
    const changes = buildPolicyChanges(form, answer, level);
    if (!hasChanges(changes)) { setSaveError('Nothing has changed.'); return; }
    const body = { ...scope, changes, reason: form.reason.trim() };
    const fingerprint = JSON.stringify(body);
    if (!attempt.current || attempt.current.fingerprint !== fingerprint) attempt.current = { fingerprint, key: newKey(), body };
    await runSave(attempt.current.body, attempt.current.key);
  };

  const completeStepUp = async () => {
    const pending = retry.current;
    retry.current = null;
    setStepUp(null);
    if (pending) await runSave(pending.body, pending.key);
  };

  return (
    <section className="cn-policy" data-testid={`cp-card-${level}`} aria-label={title}>
      <StepUpDialog
        challenge={stepUp}
        title="Verify credit settings change"
        description="Confirm your identity before changing how credit applies."
        onComplete={completeStepUp}
        onCancel={() => { retry.current = null; setStepUp(null); setSaveError('Verification was cancelled. Nothing was changed.'); }}
      />
      <header className="cn-panel-head">
        <div>
          <h4>{title}</h4>
          <p className="cn-muted">{POLICY_LEVEL_LABEL[level]}: {modeText} at {termsSummary(effective?.terms)}.</p>
        </div>
        {!editing && answer ? <button type="button" data-testid={`cp-edit-${level}`} disabled={ownAccount} onClick={startEdit}>Edit settings</button> : null}
      </header>
      {ownAccount ? <p className="cn-muted" data-testid={`cp-own-hint-${level}`}>Another billing manager must change credit on your own account.</p> : null}
      {loadError ? <p className="cn-form-error" role="alert" data-testid={`cp-load-error-${level}`}>{loadError}</p> : null}
      {notice ? <p className="cn-notice" role="status" data-testid={`cp-notice-${level}`}>{notice}</p> : null}

      {!editing && answer ? (
        <dl className="cn-facts" data-testid={`cp-summary-${level}`}>
          {customerLevel ? <div><dt>How credit applies</dt><dd data-testid="cp-state-customer">{customerStateLabel(customerStateOf(answer))}</dd></div> : null}
          <div><dt>Mode</dt><dd data-testid={`cp-mode-${level}`}>{modeText}</dd></div>
          <div><dt>Share per booking</dt><dd>{termsSummary(effective?.terms)}</dd></div>
          <div><dt>Cap per booking</dt><dd>{effective?.terms?.max_per_booking == null ? 'No cap' : `£${effective.terms.max_per_booking}`}</dd></div>
          <div><dt>Cap per invoice</dt><dd>{effective?.terms?.max_per_invoice == null ? 'No cap' : `£${effective.terms.max_per_invoice}`}</dd></div>
          <div><dt>Valid</dt><dd>{effective?.terms?.valid_from || 'Any date'} to {effective?.terms?.expires_on || 'no end date'}</dd></div>
          <div><dt>Charge types</dt><dd>{(effective?.terms?.line_kinds || []).join(', ') || 'Every hire line'}</dd></div>
          <div><dt>Set by</dt><dd>{source}</dd></div>
        </dl>
      ) : null}
      {!editing && answer && level === 'credit' && mode === AUTOMATIC_MODE ? <p className="cn-muted" data-testid={`cp-applied-${level}`}>Applied: automatically</p> : null}
      {!editing && answer && level === 'credit' && mode === MANUAL_MODE ? <p className="cn-muted" data-testid={`cp-applied-${level}`}>Applied: manually by the customer</p> : null}

      {editing && form ? (
        <form className="cn-form" onSubmit={save} noValidate data-testid={`cp-form-${level}`}>
          {customerLevel ? (
            <fieldset className="cn-application" data-testid="cp-states-customer">
              <legend>How credit applies to this customer</legend>
              {CUSTOMER_STATES.map((item) => (
                <label key={item.value}><input type="radio" name="cp-state-customer" value={item.value} data-testid={`cp-state-${item.value}-customer`} checked={form.customer_state === item.value} onChange={() => setForm((current) => ({ ...current, customer_state: item.value }))} /> {item.label}</label>
              ))}
              <small className="cn-muted" data-testid="cp-state-line-customer">{CUSTOMER_STATES.find((item) => item.value === form.customer_state)?.line}</small>
              {form.customer_state === 'custom' ? (
                <label>Mode for this customer
                  <select data-testid="cp-custom-mode-customer" value={form.custom_mode} onChange={set('custom_mode')}>
                    <option value="follow">Same as the organisation default</option>
                    <option value="auto">Auto on invoice</option>
                  </select>
                </label>
              ) : null}
            </fieldset>
          ) : (
            <>
          <label>Mode
            <select data-testid={`cp-field-mode-${level}`} value={form.mode} onChange={set('mode')}>
              {modeOptionsFor(level, form.mode).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <fieldset className="cn-application" data-testid={`cp-application-${level}`}>
            <legend>Credit application</legend>
            <label><input type="radio" name={`cp-application-${level}`} value={AUTOMATIC_MODE} data-testid={`cp-application-auto-${level}`} checked={creditApplicationOf(form.mode) === AUTOMATIC_MODE} onChange={() => setApplication(AUTOMATIC_MODE)} /> Automatic — applied to the next invoice</label>
            <label><input type="radio" name={`cp-application-${level}`} value={MANUAL_MODE} data-testid={`cp-application-manual-${level}`} checked={manual} onChange={() => setApplication(MANUAL_MODE)} aria-describedby={manual ? `cp-note-manual-${level}` : undefined} /> Manual — the customer chooses after the invoice is issued</label>
            {manual ? <small className="cn-muted" id={`cp-note-manual-${level}`} data-testid={`cp-note-manual-${level}`}>{MANUAL_LINE}</small> : null}
          </fieldset>
            </>
          )}
          {showTerms ? (
            <>
          <label>Share
            <select data-testid={`cp-field-discount-${level}`} value={form.discount_type} onChange={setShare} disabled={manual}>
              <option value="percent">Percent of the line</option>
              <option value="fixed">Fixed amount</option>
              {showInvoiceShare ? <option value={PERCENT_OF_INVOICE}>Percent of the invoice</option> : null}
            </select>
          </label>
          {isPercentShare(form.discount_type) ? (
            <>
              <label>{invoiceShare ? 'Percent of the invoice (%)' : 'Percent per booking (%)'}<input type="number" min="0.01" max="100" step="0.01" data-testid={`cp-field-percent-${level}`} value={form.percent} onChange={set('percent')} disabled={manual} aria-invalid={Boolean(errors.percent)} aria-describedby={`cp-err-percent-${level}`} /></label>
              <FieldError id={`cp-err-percent-${level}`} message={errors.percent} />
            </>
          ) : (
            <>
              <label>Fixed amount per booking (£)<input type="number" min="0.01" step="0.01" data-testid={`cp-field-fixed-${level}`} value={form.fixed_amount} onChange={set('fixed_amount')} disabled={manual} aria-invalid={Boolean(errors.fixed_amount)} aria-describedby={`cp-err-fixed-${level}`} /></label>
              <FieldError id={`cp-err-fixed-${level}`} message={errors.fixed_amount} />
            </>
          )}
          <label>Cap per booking (£, empty for none)<input type="number" min="0.01" step="0.01" data-testid={`cp-field-max-booking-${level}`} value={form.max_per_booking} onChange={set('max_per_booking')} aria-invalid={Boolean(errors.max_per_booking)} disabled={invoiceShare || manual} aria-describedby={invoiceShare ? `cp-note-max-booking-${level}` : undefined} /></label>
          {invoiceShare ? <small className="cn-muted" id={`cp-note-max-booking-${level}`} data-testid={`cp-note-max-booking-${level}`}>Not used for a percent of the invoice.</small> : <FieldError message={errors.max_per_booking} />}
          <label>Cap per invoice (£, empty for none)<input type="number" min="0.01" step="0.01" data-testid={`cp-field-max-invoice-${level}`} value={form.max_per_invoice} onChange={set('max_per_invoice')} aria-invalid={Boolean(errors.max_per_invoice)} disabled={manual} /></label>
          <FieldError message={errors.max_per_invoice} />
          <label>Valid from<input type="date" data-testid={`cp-field-valid-from-${level}`} value={form.valid_from} onChange={set('valid_from')} /></label>
          <FieldError message={errors.valid_from} />
          <label>Valid until<input type="date" data-testid={`cp-field-expires-${level}`} value={form.expires_on} onChange={set('expires_on')} aria-invalid={Boolean(errors.expires_on)} aria-describedby={`cp-err-expires-${level}`} /></label>
          <FieldError id={`cp-err-expires-${level}`} message={errors.expires_on} />
          <label>Charge types (comma separated, empty for every hire line)<input data-testid={`cp-field-line-kinds-${level}`} value={form.line_kinds} onChange={set('line_kinds')} aria-invalid={Boolean(errors.line_kinds)} /></label>
          <FieldError message={errors.line_kinds} />
            </>
          ) : null}
          <label>Reason for the change<textarea data-testid={`cp-field-reason-${level}`} value={form.reason} onChange={set('reason')} aria-invalid={Boolean(errors.reason)} aria-describedby={`cp-err-reason-${level}`} /></label>
          <FieldError id={`cp-err-reason-${level}`} message={errors.reason} />
          {saveError ? <p className="cn-form-error" role="alert" data-testid={`cp-save-error-${level}`}>{saveError}</p> : null}
          <div className="cn-actions">
            <button type="button" onClick={() => { setEditing(false); setSaveError(''); }}>Cancel</button>
            <button type="submit" className="cn-primary" data-testid={`cp-save-${level}`} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
          </div>
        </form>
      ) : null}
      {!editing && saveError ? <p className="cn-form-error" role="alert" data-testid={`cp-save-error-${level}`}>{saveError}</p> : null}
    </section>
  );
}
